import { Group } from '@mantine/core';
import { IconFiles, IconPlus, IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SerializableRenderTreeNode } from '../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { assertUnreachable, compareByName, formatToPascalCase } from '../../../../../react-commons/utils/utilFunctions';
import { when } from '../../../../../react-commons/utils/when';
import { SplitPane } from '../../../../../react-commons/views/split-pane/SplitPane';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    type ActivitiesResource,
    useActivitiesActions,
} from '../../../resource/resources/artifacts/activities/activitiesResource';
import {
    type ImageAttachmentResource,
    useImageAttachmentActions,
} from '../../../resource/resources/artifacts/attachment/imageAttachmentResource';
import {
    useVideoAttachmentActions,
    type VideoAttachmentResource,
} from '../../../resource/resources/artifacts/attachment/videoAttachmentResource';
import {
    type CriteriaResource,
    useCriteriaActions,
} from '../../../resource/resources/artifacts/criteria/criteriaResource';
import { type MethodResource, useMethodActions } from '../../../resource/resources/artifacts/method/methodResource';
import {
    type ObjectiveResource,
    useObjectiveActions,
} from '../../../resource/resources/artifacts/objective/objectiveResource';
import {
    type ProcessAreaResource,
    useProcessAreaActions,
} from '../../../resource/resources/artifacts/process_area/processAreaResource';
import {
    type ProcessArtifactResource,
    useProcessArtifactActions,
} from '../../../resource/resources/artifacts/process_artifact/processArtifactResource';
import { useProjectVersionPublishmentActions } from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import {
    type ProcessAreaRoleRelationResource,
    useProcessAreaRoleRelationActions,
} from '../../../resource/resources/artifacts/role/processAreaRoleRelationResource';
import { type RoleResource, useRoleActions } from '../../../resource/resources/artifacts/role/roleResource';
import {
    type StandardResource,
    useStandardActions,
} from '../../../resource/resources/artifacts/standard/standardResource';
import { type ToolResource, useToolActions } from '../../../resource/resources/artifacts/tool/toolResource';
import {
    useWorkflowRepresentationActions,
    type WorkflowRepresentationResource,
} from '../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import {
    type UseCaseResource,
    useUseCaseActions,
} from '../../../resource/resources/artifacts/use_case/useCaseResource';
import {
    useVerificationMetricActions,
    type VerificationMetricResource,
} from '../../../resource/resources/artifacts/verification_metric/verificationMetricResource';
import { CloneActivitiesDialog } from '../../reusable-components/dialogs/clone-dialogs/CloneActivitiesDialog';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { NothingSelectedComponent } from '../../reusable-components/NothingSelectedComponent';
import { checkUnsavedData } from '../../util/checkUnsavedData';
import { selectIsDirty } from '../../util/useSelectorFunctions';
import { ActivityCreationDialog } from './reusable-elements/activity/ActivityCreationDialog';
import { ActivityOverviewComponent } from './reusable-elements/activity/ActivityOverviewComponent';
import { ActivityView } from './reusable-elements/activity/ActivityView';
import { ImageAttachmentCreationDialog } from './reusable-elements/attachment/ImageAttachmentCreationDialog';
import { ImageAttachmentOverviewComponent } from './reusable-elements/attachment/ImageAttachmentOverviewComponent';
import { ImageAttachmentView } from './reusable-elements/attachment/ImageAttachmentView';
import { VideoAttachmentCreationDialog } from './reusable-elements/attachment/VideoAttachmentCreationDialog';
import { VideoAttachmentOverviewComponent } from './reusable-elements/attachment/VideoAttachmentOverviewComponent';
import { VideoAttachmentView } from './reusable-elements/attachment/VideoAttachmentView';
import { CriteriaCreationDialog } from './reusable-elements/criteria/CriteriaCreationDialog';
import { CriteriaOverviewComponent } from './reusable-elements/criteria/CriteriaOverviewComponent';
import { CriteriaView } from './reusable-elements/criteria/CriteriaView';
import { MethodCreationDialog } from './reusable-elements/method/MethodCreationDialog';
import { MethodOverviewComponent } from './reusable-elements/method/MethodOverviewComponent';
import { MethodView } from './reusable-elements/method/MethodView';
import { ObjectiveCreationDialog } from './reusable-elements/objective/ObjectiveCreationDialog';
import { ObjectiveOverviewComponent } from './reusable-elements/objective/ObjectiveOverviewComponent';
import { ObjectiveView } from './reusable-elements/objective/ObjectiveView';
import { ProcessAreaCreationDialog } from './reusable-elements/process-area/ProcessAreaCreationDialog';
import { ProcessAreaOverviewComponent } from './reusable-elements/process-area/ProcessAreaOverviewComponent';
import { ProcessAreaView } from './reusable-elements/process-area/ProcessAreaView';
import { ProcessArtifactCreationDialog } from './reusable-elements/process-artifact/ProcessArtifactCreationDialog';
import { ProcessArtifactOverviewComponent } from './reusable-elements/process-artifact/ProcessArtifactOverviewComponent';
import { ProcessArtifactView } from './reusable-elements/process-artifact/ProcessArtifactView';
import { RoleCreationDialog } from './reusable-elements/role/RoleCreationDialog';
import { RoleOverviewComponent } from './reusable-elements/role/RoleOverviewComponent';
import { RoleView } from './reusable-elements/role/RoleView';
import { ProcessAreaRoleRelationCreationDialog } from './reusable-elements/role-relation/ProcessAreaRoleRelationCreationDialog';
import { ProcessAreaRoleRelationOverviewComponent } from './reusable-elements/role-relation/ProcessAreaRoleRelationOverviewComponent';
import { ProcessAreaRoleRelationView } from './reusable-elements/role-relation/ProcessAreaRoleRelationView';
import { StandardCreationDialog } from './reusable-elements/standard/StandardCreationDialog';
import { StandardOverviewComponent } from './reusable-elements/standard/StandardOverviewComponent';
import { StandardView } from './reusable-elements/standard/StandardView';
import { ToolCreationDialog } from './reusable-elements/tool/ToolCreationDialog';
import { ToolOverviewComponent } from './reusable-elements/tool/ToolOverviewComponent';
import { ToolView } from './reusable-elements/tool/ToolView';
import { UseCaseCreationDialog } from './reusable-elements/use-case/UseCaseCreationDialog';
import { UseCaseOverviewComponent } from './reusable-elements/use-case/UseCaseOverviewComponent';
import { UseCaseView } from './reusable-elements/use-case/UseCaseView';
import { VerificationMetricCreationDialog } from './reusable-elements/verification-metric/VerificationMetricCreationDialog';
import { VerificationMetricOverviewComponent } from './reusable-elements/verification-metric/VerificationMetricOverviewComponent';
import { VerificationMetricView } from './reusable-elements/verification-metric/VerificationMetricView';
import { WorkflowRepresentationCreationDialog } from './reusable-elements/workflow-representation/WorkflowRepresentationCreationDialog';
import { WorkflowRepresentationOverviewComponent } from './reusable-elements/workflow-representation/WorkflowRepresentationOverviewComponent';
import { WorkflowRepresentationView } from './reusable-elements/workflow-representation/WorkflowRepresentationView';

type ProjectReusableElementsComponentTreeNodeData =
    | {
          resourceType: ResourceType.STANDARD;
          resource?: StandardResource;
      }
    | {
          resourceType: ResourceType.USE_CASE;
          resource?: UseCaseResource;
      }
    | {
          resourceType: ResourceType.WORKFLOW_REPRESENTATION;
          resource?: WorkflowRepresentationResource;
      }
    | {
          resourceType: ResourceType.PROCESS_AREA;
          resource?: ProcessAreaResource;
      }
    | {
          resourceType: ResourceType.TOOL;
          resource?: ToolResource;
      }
    | {
          resourceType: ResourceType.ROLE;
          resource?: RoleResource;
      }
    | {
          resourceType: ResourceType.PROCESS_AREA_ROLE_RELATION;
          resource?: ProcessAreaRoleRelationResource;
      }
    | {
          resourceType: ResourceType.VERIFICATION_METRIC;
          resource?: VerificationMetricResource;
      }
    | {
          resourceType: ResourceType.METHOD;
          resource?: MethodResource;
      }
    | {
          resourceType: ResourceType.PROCESS_ARTIFACT;
          resource?: ProcessArtifactResource;
      }
    | {
          resourceType: ResourceType.OBJECTIVE;
          resource?: ObjectiveResource;
      }
    | {
          resourceType: ResourceType.CRITERIA;
          resource?: CriteriaResource;
      }
    | {
          resourceType: ResourceType.ACTIVITIES;
          resource?: ActivitiesResource;
      }
    | {
          resourceType: ResourceType.IMAGE_ATTACHMENT;
          resource?: ImageAttachmentResource;
      }
    | {
          resourceType: ResourceType.VIDEO_ATTACHMENT;
          resource?: VideoAttachmentResource;
      };

type ProjectReusableElementsComponentTreeNode = SerializableRenderTreeNode<
    ProjectReusableElementsComponentTreeNodeData | undefined
>;

const parseTree = (
    standardResourcesQuery: QueryObserverResult<Array<StandardResource> | null>,
    useCaseResourcesQuery: QueryObserverResult<Array<UseCaseResource> | null>,
    workflowRepresentationResourcesQuery: QueryObserverResult<Array<WorkflowRepresentationResource> | null>,
    processAreaResourcesQuery: QueryObserverResult<Array<ProcessAreaResource> | null>,
    toolResourcesQuery: QueryObserverResult<Array<ToolResource> | null>,
    roleResourcesQuery: QueryObserverResult<Array<RoleResource> | null>,
    roleRelationResourcesQuery: QueryObserverResult<Array<ProcessAreaRoleRelationResource> | null>,
    verificationMetricResourcesQuery: QueryObserverResult<Array<VerificationMetricResource> | null>,
    methodResourcesQuery: QueryObserverResult<Array<MethodResource> | null>,
    processArtifactResourcesQuery: QueryObserverResult<Array<ProcessArtifactResource> | null>,
    objectiveResourcesQuery: QueryObserverResult<Array<ObjectiveResource> | null>,
    criteriaResourcesQuery: QueryObserverResult<Array<CriteriaResource> | null>,
    activitiesResourcesQuery: QueryObserverResult<Array<ActivitiesResource> | null>,
    imageAttachmentResourcesQuery: QueryObserverResult<Array<ImageAttachmentResource> | null>,
    videoAttachmentResourcesQuery: QueryObserverResult<Array<VideoAttachmentResource> | null>
): Array<ProjectReusableElementsComponentTreeNode> => {
    const getGroupNode = <RT extends ProjectReusableElementsComponentTreeNodeData['resourceType']>(
        data:
            | Array<
                  NonNullable<Extract<ProjectReusableElementsComponentTreeNodeData, { resourceType: RT }>['resource']>
              >
            | null
            | undefined,
        resourceType: RT,
        label?: string
    ) => ({
        key: resourceType,
        label: `${label ?? `${formatToPascalCase(resourceType)}s`} (${data?.length ?? 0})`,
        data: { resourceType },
        children: data?.toSorted(compareByName).map((resource) => ({
            key: resource.id,
            label: resource.name,
            data: { resourceType, resource },
        })),
    });

    const standardsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        standardResourcesQuery.data,
        ResourceType.STANDARD
    );

    const useCasesNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        useCaseResourcesQuery.data,
        ResourceType.USE_CASE
    );

    const workflowRepresentationsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        workflowRepresentationResourcesQuery.data,
        ResourceType.WORKFLOW_REPRESENTATION
    );

    const processAreasNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        processAreaResourcesQuery.data,
        ResourceType.PROCESS_AREA
    );

    const toolsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        toolResourcesQuery.data,
        ResourceType.TOOL
    );

    const rolesNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        roleResourcesQuery.data,
        ResourceType.ROLE
    );

    const roleRelationsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        roleRelationResourcesQuery.data,
        ResourceType.PROCESS_AREA_ROLE_RELATION,
        'Role Relations'
    );

    const verificationMetricsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        verificationMetricResourcesQuery.data,
        ResourceType.VERIFICATION_METRIC
    );

    const methodsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        methodResourcesQuery.data,
        ResourceType.METHOD
    );

    const processArtifactsNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        processArtifactResourcesQuery.data,
        ResourceType.PROCESS_ARTIFACT
    );

    const objectivesNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        objectiveResourcesQuery.data,
        ResourceType.OBJECTIVE
    );

    const criteriasNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        criteriaResourcesQuery.data,
        ResourceType.CRITERIA
    );

    const imageAttachmentNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        imageAttachmentResourcesQuery.data,
        ResourceType.IMAGE_ATTACHMENT
    );

    const videoAttachmentNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        videoAttachmentResourcesQuery.data,
        ResourceType.VIDEO_ATTACHMENT
    );

    const activitiesNode: ProjectReusableElementsComponentTreeNode = getGroupNode(
        activitiesResourcesQuery.data,
        ResourceType.ACTIVITIES,
        'Activities'
    );

    const children = [
        standardsNode,
        useCasesNode,
        workflowRepresentationsNode,
        processAreasNode,
        toolsNode,
        rolesNode,
        roleRelationsNode,
        verificationMetricsNode,
        methodsNode,
        processArtifactsNode,
        objectivesNode,
        criteriasNode,
        activitiesNode,
        imageAttachmentNode,
        videoAttachmentNode,
    ];

    return [
        {
            key: ResourceType.PROJECT,
            label: 'Reusable Elements',
            data: undefined,
            children,
        },
    ];
};

export const ProjectReusableElementsComponent = ({
    showing,
    projectId,
    projectVersionId,
}: {
    projectId: string | undefined;
    projectVersionId: string | undefined;
    showing: string | null;
}) => {
    const dispatch = useDispatch();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const nestedDialog = useNestedDialogFunction();

    const [selectedNode, setSelectedNode] = React.useState<ProjectReusableElementsComponentTreeNode | null>(null);
    const [selectedNodeKey, setSelectedNodeKey] = React.useState<string>('');
    const [nodes, setNodes] = React.useState<Array<ProjectReusableElementsComponentTreeNode>>([]);
    const [expandedKeys, setExpandedKeys] = React.useState<Array<string>>([]);
    const isDirty = useSelector(selectIsDirty);

    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const standardActions = useStandardActions();
    const useCaseActions = useUseCaseActions();
    const workflowRepresentationActions = useWorkflowRepresentationActions();
    const processAreaActions = useProcessAreaActions();
    const toolActions = useToolActions();
    const roleActions = useRoleActions();
    const roleRelationActions = useProcessAreaRoleRelationActions();
    const verificationMetricActions = useVerificationMetricActions();
    const methodActions = useMethodActions();
    const processArtifactActions = useProcessArtifactActions();
    const objectiveActions = useObjectiveActions();
    const criteriaActions = useCriteriaActions();
    const activitiesActions = useActivitiesActions();
    const imageAttachmentActions = useImageAttachmentActions();
    const videoAttachmentActions = useVideoAttachmentActions();

    const standardResourcesQuery = standardActions.useGetAllResources(projectVersionId);
    const useCaseResourcesQuery = useCaseActions.useGetAllResources(projectVersionId);
    const workflowRepresentationResourcesQuery = workflowRepresentationActions.useGetAllResources(projectVersionId);
    const processAreaResourceQuery = processAreaActions.useGetAllResources(projectVersionId);
    const toolResourcesQuery = toolActions.useGetAllResources(projectVersionId);
    const roleResourcesQuery = roleActions.useGetAllResources(projectVersionId);
    const roleRelationResourcesQuery = roleRelationActions.useGetAllResources(projectVersionId);
    const verificationMetricResourcesQuery = verificationMetricActions.useGetAllResources(projectVersionId);
    const methodResourcesQuery = methodActions.useGetAllResources(projectVersionId);
    const processArtifactResourcesQuery = processArtifactActions.useGetAllResources(projectVersionId);
    const objectiveResourcesQuery = objectiveActions.useGetAllResources(projectVersionId);
    const criteriaResourcesQuery = criteriaActions.useGetAllResources(projectVersionId);
    const activitiesResourcesQuery = activitiesActions.useGetAllResources(projectVersionId);
    const imageAttachmentResourcesQuery = imageAttachmentActions.useGetAllResourcesByExtract(projectVersionId);
    const videoAttachmentResourcesQuery = videoAttachmentActions.useGetAllResources(projectVersionId);
    const projectVersionPublishment = projectVersionPublishmentActions.useGetSingleResource(projectVersionId);

    const deleteStandard = standardActions.useDeleteResource();
    const deleteUseCase = useCaseActions.useDeleteResource();
    const deleteWorkflowRepresentation = workflowRepresentationActions.useDeleteResource();
    const deleteProcessArea = processAreaActions.useDeleteResource();
    const deleteTool = toolActions.useDeleteResource();
    const deleteRole = roleActions.useDeleteResource();
    const deleteRoleRelation = roleRelationActions.useDeleteResource();
    const deleteVerificationMetric = verificationMetricActions.useDeleteResource();
    const deleteMethod = methodActions.useDeleteResource();
    const deleteProcessArtifact = processArtifactActions.useDeleteResource();
    const deleteObjective = objectiveActions.useDeleteResource();
    const deleteCriteria = criteriaActions.useDeleteResource();
    const deleteActivity = activitiesActions.useDeleteResource();
    const deleteImageAttachment = imageAttachmentActions.useDeleteResource();
    const deleteVideoAttachment = videoAttachmentActions.useDeleteResource();

    React.useEffect(() => {
        const newNodes = parseTree(
            standardResourcesQuery,
            useCaseResourcesQuery,
            workflowRepresentationResourcesQuery,
            processAreaResourceQuery,
            toolResourcesQuery,
            roleResourcesQuery,
            roleRelationResourcesQuery,
            verificationMetricResourcesQuery,
            methodResourcesQuery,
            processArtifactResourcesQuery,
            objectiveResourcesQuery,
            criteriaResourcesQuery,
            activitiesResourcesQuery,
            imageAttachmentResourcesQuery,
            videoAttachmentResourcesQuery
        );
        setNodes(newNodes);
    }, [
        projectVersionId,
        isDirty,
        standardResourcesQuery.data,
        useCaseResourcesQuery.data,
        workflowRepresentationResourcesQuery.data,
        processAreaResourceQuery.data,
        toolResourcesQuery.data,
        roleResourcesQuery.data,
        roleRelationResourcesQuery.data,
        verificationMetricResourcesQuery.data,
        methodResourcesQuery.data,
        processArtifactResourcesQuery.data,
        objectiveResourcesQuery.data,
        criteriaResourcesQuery.data,
        activitiesResourcesQuery.data,
        imageAttachmentResourcesQuery.data,
        videoAttachmentResourcesQuery.data,
    ]);

    const deleteResource = (id: string, name: string, resourceType: ResourceType) => {
        const deleteMutateFunction = when({
            expression: resourceType,
            cases: {
                [ResourceType.STANDARD]: deleteStandard,
                [ResourceType.USE_CASE]: deleteUseCase,
                [ResourceType.WORKFLOW_REPRESENTATION]: deleteWorkflowRepresentation,
                [ResourceType.PROCESS_AREA]: deleteProcessArea,
                [ResourceType.TOOL]: deleteTool,
                [ResourceType.ROLE]: deleteRole,
                [ResourceType.PROCESS_AREA_ROLE_RELATION]: deleteRoleRelation,
                [ResourceType.VERIFICATION_METRIC]: deleteVerificationMetric,
                [ResourceType.METHOD]: deleteMethod,
                [ResourceType.PROCESS_ARTIFACT]: deleteProcessArtifact,
                [ResourceType.OBJECTIVE]: deleteObjective,
                [ResourceType.CRITERIA]: deleteCriteria,
                [ResourceType.ACTIVITIES]: deleteActivity,
                [ResourceType.IMAGE_ATTACHMENT]: deleteImageAttachment,
                [ResourceType.VIDEO_ATTACHMENT]: deleteVideoAttachment,
            },
            fallback: undefined,
        });

        if (deleteMutateFunction) {
            deleteResourceDialogFunction.deleteResource(id, name, resourceType, deleteMutateFunction, () =>
                setSelectedNode(null)
            );
        }
    };

    const getOnCreated = () => (id: string, _objectId: string) => {
        setSelectedNodeKey(id);
    };

    const openResourceCreationDialog = (resourceType: ResourceType) => {
        const dialog = when({
            expression: resourceType,
            cases: {
                [ResourceType.STANDARD]: (
                    <StandardCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.USE_CASE]: (
                    <UseCaseCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.WORKFLOW_REPRESENTATION]: (
                    <WorkflowRepresentationCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.PROCESS_AREA]: (
                    <ProcessAreaCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.TOOL]: (
                    <ToolCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.ROLE]: (
                    <RoleCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.PROCESS_AREA_ROLE_RELATION]: (
                    <ProcessAreaRoleRelationCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.VERIFICATION_METRIC]: (
                    <VerificationMetricCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.METHOD]: (
                    <MethodCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.PROCESS_ARTIFACT]: (
                    <ProcessArtifactCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.OBJECTIVE]: (
                    <ObjectiveCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.CRITERIA]: (
                    <CriteriaCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.ACTIVITIES]: (
                    <ActivityCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.IMAGE_ATTACHMENT]: (
                    <ImageAttachmentCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
                [ResourceType.VIDEO_ATTACHMENT]: (
                    <VideoAttachmentCreationDialog
                        parentId={projectVersionId ?? ''}
                        onClose={nestedDialog.closeNestedDialog}
                        onCreated={getOnCreated}
                    />
                ),
            },
            fallback: undefined,
        });

        if (dialog) {
            nestedDialog.showNestedDialog(dialog);
        }
    };

    const getResourceDisplay = () => {
        if (!selectedNode?.data) {
            return <NothingSelectedComponent />;
        }

        switch (selectedNode.data.resourceType) {
            case ResourceType.STANDARD: {
                if (selectedNode.data.resource) {
                    return <StandardView id={selectedNode.data.resource.id} />;
                }
                return <StandardOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.USE_CASE: {
                if (selectedNode.data.resource) {
                    return <UseCaseView id={selectedNode.data.resource.id} />;
                }
                return <UseCaseOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.WORKFLOW_REPRESENTATION: {
                if (selectedNode.data.resource) {
                    return (
                        <WorkflowRepresentationView
                            id={selectedNode.data.resource.id}
                            projectId={projectId}
                            projectVersionId={projectVersionId}
                        />
                    );
                }
                return <WorkflowRepresentationOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.PROCESS_AREA: {
                if (selectedNode.data.resource) {
                    return (
                        <ProcessAreaView
                            id={selectedNode.data.resource.id}
                            projectId={projectId}
                            projectVersionId={projectVersionId}
                        />
                    );
                }
                return <ProcessAreaOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.TOOL: {
                if (selectedNode.data.resource) {
                    return <ToolView id={selectedNode.data.resource.id} />;
                }
                return <ToolOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.ROLE: {
                if (selectedNode.data.resource) {
                    return <RoleView id={selectedNode.data.resource.id} />;
                }
                return <RoleOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.PROCESS_AREA_ROLE_RELATION: {
                if (selectedNode.data.resource) {
                    return <ProcessAreaRoleRelationView id={selectedNode.data.resource.id} />;
                }
                return <ProcessAreaRoleRelationOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.VERIFICATION_METRIC: {
                if (selectedNode.data.resource) {
                    return <VerificationMetricView id={selectedNode.data.resource.id} />;
                }
                return <VerificationMetricOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.METHOD: {
                if (selectedNode.data.resource) {
                    return <MethodView id={selectedNode.data.resource.id} />;
                }
                return <MethodOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.PROCESS_ARTIFACT: {
                if (selectedNode.data.resource) {
                    return <ProcessArtifactView id={selectedNode.data.resource.id} />;
                }
                return <ProcessArtifactOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.OBJECTIVE: {
                if (selectedNode.data.resource) {
                    return <ObjectiveView id={selectedNode.data.resource.id} />;
                }
                return <ObjectiveOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.CRITERIA: {
                if (selectedNode.data.resource) {
                    return <CriteriaView id={selectedNode.data.resource.id} />;
                }
                return <CriteriaOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.IMAGE_ATTACHMENT: {
                if (selectedNode.data.resource) {
                    return <ImageAttachmentView id={selectedNode.data.resource.id} />;
                }
                return <ImageAttachmentOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.VIDEO_ATTACHMENT: {
                if (selectedNode.data.resource) {
                    return <VideoAttachmentView id={selectedNode.data.resource.id} />;
                }
                return <VideoAttachmentOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            case ResourceType.ACTIVITIES: {
                if (selectedNode.data.resource) {
                    return (
                        <ActivityView
                            id={selectedNode.data.resource.id}
                            projectId={projectId}
                            projectVersionId={projectVersionId}
                        />
                    );
                }
                return <ActivityOverviewComponent parentId={projectVersionId ?? ''} />;
            }
            default: {
                return assertUnreachable(selectedNode.data);
            }
        }
    };

    return (
        <div
            style={{
                height: projectVersionPublishment.data ? 'calc(100vh - 370px)' : 'calc(100vh - 320px)',
                paddingTop: 4,
            }}
        >
            <SplitPane key={showing} secondaryInitialSize={70}>
                <div>
                    <Tree
                        nodes={nodes}
                        filtered
                        rootExpanded
                        expandedKeys={expandedKeys}
                        selectedNodeKey={selectedNodeKey}
                        onExpandedChanged={(selectedKeys) => setExpandedKeys(selectedKeys as Array<string>)}
                        onNodeSelected={(node) => {
                            checkUnsavedData(isDirty, () => {
                                setSelectedNode(node);
                                setSelectedNodeKey(node.key);
                            });
                        }}
                        getElements={(node) => {
                            if (!node.data) {
                                return {};
                            }

                            const { resource, resourceType } = node.data;

                            return {
                                hoverNode:
                                    resourceType === ResourceType.ACTIVITIES && !resource ?
                                        <Group gap={0}>
                                            <TreeHoverButton
                                                Icon={IconPlus}
                                                onClick={() => openResourceCreationDialog(resourceType)}
                                            />
                                            <TreeHoverButton
                                                Icon={IconFiles}
                                                onClick={() =>
                                                    nestedDialog.showNestedDialog(
                                                        <CloneActivitiesDialog
                                                            onClose={nestedDialog.closeNestedDialog}
                                                            projectId={projectId ?? ''}
                                                            projectVersionId={projectVersionId ?? ''}
                                                        />
                                                    )
                                                }
                                                tooltip={{ text: 'Copy Activities from Version...' }}
                                            />
                                        </Group>
                                    : resource ?
                                        <TreeHoverButton
                                            Icon={IconTrash}
                                            onClick={() => deleteResource(resource.id, resource.name, resourceType)}
                                        />
                                    :   <TreeHoverButton
                                            Icon={IconPlus}
                                            onClick={() => openResourceCreationDialog(resourceType)}
                                        />,
                            };
                        }}
                    />
                </div>
                <div className={'right-side-display'}>{getResourceDisplay()}</div>
            </SplitPane>
        </div>
    );
};
